import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Custom hook to handle mobile detection and Redux state updates
 * This hook listens for window resize events and updates the isMobile state
 *
 * @param {number} breakpoint - The width breakpoint for mobile detection (default: 900)
 * @returns {boolean} Current mobile state
 */
export const useMobileDetection = (breakpoint = 900) => {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.isMobile);

  // Debounced function to check if window is mobile size
  const checkIsMobile = useCallback(() => {
    const newIsMobile = window.innerWidth < breakpoint;

    // Only dispatch if the value has changed to avoid unnecessary re-renders
    if (newIsMobile !== isMobile) {
      dispatch({ type: "isMobile/update", payload: newIsMobile });
    }
  }, [dispatch, breakpoint, isMobile]);

  useEffect(() => {
    // Check on mount to ensure correct initial state
    checkIsMobile();

    // Debounce the resize handler to improve performance
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsMobile, 150); // 150ms debounce
    };

    // Add resize listener
    window.addEventListener("resize", debouncedResize);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [checkIsMobile]);

  return isMobile;
};
